import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { Customer } from 'src/app/models/customer';
import { CustomerType } from 'src/app/models/customer-type';
import { Rights } from 'src/app/models/enums/rights';
import { CustomersService } from 'src/app/services/customers.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';

@Component({
    selector: 'app-customers-list',
    templateUrl: './customers-list.component.html',
    styleUrls: ['./customers-list.component.scss'],
    imports: [AdvancedGridviewComponent]
})
export class CustomersListComponent extends DefaultListComponent<Customer> implements OnInit {
    /**
     * Configuration des colonnes de la liste.
     */
    tableConfig: ColumnDefinition[] = null;

    /**
     * Constructeur
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     * @param enumHelper : Classe utilitaire pour les énumérations.
     * @param customersService : Service de gestion des clients.
     */
    constructor(
        public override dialog: MatDialog,
        protected override memoryService: MemoryService,
        public enumHelper: EnumHelper,
        private customersService: CustomersService,
        private spinner: NgxSpinnerService,
        private notificationService: NotificationService
    ) {
        super(dialog, memoryService);

        // Specifie des colonnes en fonction des droits utilisateurs
        if (this.memoryService.getConnectedUser().params.idVisible === true) {
            this.tableConfig = [
                new ColumnDefinition('id', 'Id', ColumnType.Numeric, FilterType.Text, 1, 5, true),
                new ColumnDefinition('code', 'Code', ColumnType.Text, FilterType.Text, 2, 10, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 3, 20, true),
                new ColumnDefinition('address1', 'Adresse 1', ColumnType.Text, FilterType.Text, 4, 20, true),
                new ColumnDefinition('postalCode', 'Code Postal', ColumnType.Text, FilterType.Text, 5, 10, true),
                new ColumnDefinition('city', 'Ville', ColumnType.Text, FilterType.Text, 6, 10, true),
                new ColumnDefinition('country', 'Pays', ColumnType.Text, FilterType.Text, 7, 10, true),
                new ColumnListDefinition('type', 'Type', FilterType.List, 8, 10, true, false),
            ];
        } else {
            this.tableConfig = [
                new ColumnDefinition('code', 'Code', ColumnType.Text, FilterType.Text, 2, 5, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 3, 20, true),
                new ColumnDefinition('address1', 'Adresse 1', ColumnType.Text, FilterType.Text, 4, 20, true),
                new ColumnDefinition('postalCode', 'Code Postal', ColumnType.Text, FilterType.Text, 5, 10, true),
                new ColumnDefinition('city', 'Ville', ColumnType.Text, FilterType.Text, 6, 15, true),
                new ColumnDefinition('country', 'Pays', ColumnType.Text, FilterType.Text, 7, 15, true),
                new ColumnListDefinition('type', 'Type', FilterType.List, 8, 10, true, false),
            ];
        }

        // Spécifie les éléments du menu contextuel en fonction des droits utilisateur
        this.AddMenuItem('Dupliquer', MenuITemAction.Duplicate, Rights.CustomerCreate);
        this.AddMenuItem('Éditer', MenuITemAction.Update, Rights.CustomerEdit);
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, Rights.CustomerDelete);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.dataSource = new AdvancedDataSource(null, this.customersService, this.spinner, this.notificationService);
        this.filteredTableModel = new AdvancedGridviewModel(this.tableConfig, new SortInfos('code', 'asc'));

        // Ajout des listes déroulantes
        // attente de 500ms pour que la gridView ait le temps de se remplir.
        setTimeout(() => {
            this.loadListColumnFilterDatas();
        }, 500);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<Customer>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.itemDisplayed.emit(event.context);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.itemEdited.emit(event.context);
        } else if (event.action == MenuITemAction.Duplicate) {
            // Cas de duplication.
            this.itemDuplicated.emit(event.context);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(`Vous êtes sur le point de supprimer le client '${event.context.name}'.`, event.context);
        }
    }

    /**
     * Charge les éléments des filtres des colonnes de type liste.
     */
    private loadListColumnFilterDatas(): void {
        const typeKeys = Object.keys(CustomerType);
        var listItem: ListItem<string>[] = [];
        const length = typeKeys.length / 2;

        for (let i = 1; i <= length; i++) {
            listItem.push(new ListItem(typeKeys[i - 1], typeKeys[i + length - 1]));
        }

        this.loadColumn(
            this.filteredTableModel.displayedColumnsDefinitions.find(
                (x) => this.enumHelper.areEquals(ColumnType, x.type, ColumnType.List) && x.columnName === 'type'
            ) as ColumnListDefinition<string>,
            listItem
        );
    }
}
